/**
 * Painéis do Presidente nas regiões do AppShell (§7.1, §4.4.2, §4.6.2; FE §5): "Por região" (§7.12), "Ao longo da
 * apuração" (§7.13), a chance em foco no computador largo (§7.14 R1 nova; chanceFocusSlot.tsx) e "Últimas
 * atualizações" (§7.15). Fica fora do App.tsx, como o herói (heroSlot.tsx), o mapa (mapSlot.tsx) e os cartões
 * (placesSlot.tsx), para as fases não disputarem as mesmas linhas.
 *
 * Onde cada um aparece:
 * - `TrendSlot` (coluna esquerda no desktop e na TV, logo depois do herói; seção "Mais" do `flow`): o `TrendChart`
 *   sozinho, na `ErrorBoundary name="trend"` (§7.1). Só na aba Presidente. A chance não mora mais aqui (DECISOES §71).
 * - `RightPanels` (coluna direita do desktop): com lugar ou perfil em foco, só o cartão do §8 (`PlaceSlot`; a chance
 *   em foco some, ORD-F-104); sem foco, no topo a chance em foco (só no computador largo, na aba Presidente) e depois
 *   `RegionPanel` + `UpdatesFeed` na aba Presidente e só o `UpdatesFeed` nas outras (AC-4.4-04); abaixo do feed, a
 *   Situação incomum (`InsightsSlot`, só com a camada no snapshot; docs/DECISOES.md §33). Acima do feed, os
 *   comentários ao vivo (`CommentarySlot`, P-32; só com `features.commentary` e a camada no snapshot); no celular, eles
 *   abrem a seção "Mais sobre a apuração" (proposta do front de 14/10, seção 2.1).
 * - `MorePanels` (seção "Mais sobre a apuração" do `flow`, só sem foco; §4.6.2): `RegionPanel` → `TrendSlot` →
 *   `UpdatesFeed` na aba Presidente; só o `UpdatesFeed` nas outras; a Situação incomum por último, nas duas.
 * Tablet e `land` não têm painéis (§7.1 "Ordem no celular"); a TV não tem `RegionPanel` nem feed (§4.8.2).
 * - `PanelsIntro` (nó sem visual): a marca `data-intro` no `<html>` durante a abertura, para os atrasos de entrada
 *   das colunas (§7.1 bloco 5; panelsSlot.css).
 */
import './panelsSlot.css';
import type { ComponentChildren } from 'preact';
import { useLayoutEffect, useState } from 'preact/hooks';
import { SegmentedControl } from '../components/SegmentedControl';
import { t } from '../copy/strings';
import { ErrorBoundary } from '../features/errors/ErrorBoundary';
import { RegionPanel } from '../features/regions/RegionPanel';
import { TrendChart } from '../features/trend/TrendChart';
import { UpdatesFeed } from '../features/updates/UpdatesFeed';
import { useHasFocus, useLayout, useRouteState } from '../state/hooks';
import { historySeries, layout } from '../state/store';
import { ChanceFocusSlot, useChanceFocusPlace } from './chanceFocusSlot';
import { CHANCE_LAYOUT } from './chanceLayout';
import { CommentarySlot, useCommentaryShown } from './commentarySlot';
import { firstPaintDone } from './firstPaint';
import { INTRO_WINDOW_MS } from './heroSlot';
import { InsightsSlot } from './insightsSlot';
import { PlaceSlot } from './placesSlot';

/** Maior atraso de entrada da abertura (2º cartão da direita, §7.1 bloco 5). */
const LAST_INTRO_DELAY_MS = 760;
/** Duração da entrada (`--dur-enter`, §3.6). */
const ENTER_MS = 500;

/**
 * Marca `data-intro` no `<html>` (§7.1 bloco 5, A02 de §3.6): na montagem, dentro da janela da abertura (1,8 s desde o
 * início da navegação) e só no desktop e na TV. A marca sai quando a última entrada atrasada termina, para nenhum
 * cartão montado depois (outro lugar, outra aba) herdar o atraso.
 */
export function PanelsIntro() {
  useLayoutEffect(() => {
    const html = document.documentElement;
    const { mode } = layout.peek();
    const now = performance.now();
    if ((mode !== 'desktop' && mode !== 'tv') || now >= INTRO_WINDOW_MS) return;
    html.setAttribute('data-intro', '');
    const id = setTimeout(
      () => html.removeAttribute('data-intro'),
      INTRO_WINDOW_MS + LAST_INTRO_DELAY_MS + ENTER_MS - now,
    );
    return () => {
      clearTimeout(id);
      html.removeAttribute('data-intro');
    };
  }, []);
  return null;
}

/**
 * "Ao longo da apuração" (§7.1, §7.13). A chance saiu daqui para o topo da apuração (§7.14 R1 nova, DECISOES §71): o
 * gráfico fica sozinho no lugar dele. O próprio `TrendChart` não monta em `data-h` short/tiny (nem no tablet e no
 * `land`). No computador estreito com a chance em foco no cartão principal, o gráfico sai da coluna esquerda: o
 * cartão cresce e o gráfico `grow` ficaria espremido (a coluna rola, panelsSlot.css).
 */
export function TrendSlot() {
  const route = useRouteState();
  const { mode } = useLayout();
  const chance = useChanceFocusPlace();
  const points = historySeries.value;
  const president = route.office === 'president';
  // o gráfico não é o conteúdo principal da 1ª tela: monta depois da 1ª pintura (DECISOES §38)
  if (!president || !firstPaintDone.value) return null;
  // ORD-F-151 A: o palco acima do herói no computador estreito (`top`) também tira o gráfico da coluna
  if (mode === 'desktop' && (chance === 'hero' || chance === 'top')) return null;
  return (
    <ErrorBoundary name="trend">
      <TrendChart points={points} />
    </ErrorBoundary>
  );
}

/** "Por região" (§7.12) na sua boundary (§7.1). Só na aba Presidente (AC-4.4-04). */
function RegionsSlot() {
  return (
    <ErrorBoundary name="regions">
      <RegionPanel />
    </ErrorBoundary>
  );
}

/** "Últimas atualizações" (§7.15) na sua boundary (§7.1). */
function UpdatesSlot() {
  return (
    <ErrorBoundary name="updates">
      <UpdatesFeed />
    </ErrorBoundary>
  );
}

type FeedTab = 'comments' | 'regions' | 'updates';

/**
 * Coluna direita do desktop (§4.4.2): cartão do foco, ou os painéis sem foco. Com os comentários no ar, o quadro de
 * "Últimas atualizações" ganha duas abas, Comentários (padrão) e Atualizações (proposta do front de 14/10, seção 2.1):
 * os dois cartões empilhados não cabiam na altura da coluna (achado no ensaio de 16/10, 1440×900).
 *
 * Página do replay (`commentary`, o painel dos comentários da noite; P-36): três abas, Comentários (padrão) · Por região
 * · Atualizações, porque o cartão das lentes ocupa o alto da coluna e "Por região" não deixava espaço (§8 de 16/10,
 * ~17h55; ORD-F-020, item 1). Sem o painel, a coluna fica como antes.
 */
export function RightPanels({ commentary = null }: { commentary?: ComponentChildren | null } = {}) {
  const hasFocus = useHasFocus();
  const route = useRouteState();
  const liveOn = useCommentaryShown();
  const { heightClass } = useLayout();
  // ORD-F-151 A (palco): com a chance no topo da coluna, "Por região" e o feed vão para abas abaixo dela
  const stage = useChanceFocusPlace() === 'right' && CHANCE_LAYOUT === 'A';
  const commentaryOn = commentary !== null || liveOn;
  const [tab, setTab] = useState<FeedTab>('comments');
  // a chance em foco no topo da coluna (computador largo; §7.14 R1 nova, DECISOES §71); com lugar em foco ela some
  // (ORD-F-104 item 4: a chance nacional acima do resultado de uma cidade seria lida como chance na cidade)
  const chance = <ChanceFocusSlot region="right" />;
  if (hasFocus) return <PlaceSlot />;
  // sem foco, a coluna direita (região, feed, Situação incomum) monta depois da 1ª pintura (DECISOES §38)
  if (!firstPaintDone.value) return null;
  const regions = route.office === 'president' ? <RegionsSlot /> : null;
  // no replay, "Por região" vira a aba do meio (só na aba Presidente, AC-4.4-04); na janela baixa o painel já é podado
  // (§4.5.2, `u-mid-up`), e a aba some com ele, para não abrir vazia. ORD-F-151 A: também com o palco no topo da coluna
  const regionsTab =
    (commentary !== null || stage) && regions !== null && (heightClass === 'mid' || heightClass === 'tall');
  if (!commentaryOn && !regionsTab)
    return (
      <>
        {chance}
        {regions}
        <UpdatesSlot />
        <InsightsSlot />
      </>
    );
  const first: FeedTab = commentaryOn ? 'comments' : regionsTab ? 'regions' : 'updates';
  const shown: FeedTab = (tab === 'regions' && !regionsTab) || (tab === 'comments' && !commentaryOn) ? first : tab;
  return (
    <>
      {chance}
      {regionsTab ? null : regions}
      <SegmentedControl<FeedTab>
        options={[
          ...(commentaryOn ? [{ id: 'comments' as const, label: t('panel.tab.comments') }] : []),
          ...(regionsTab ? [{ id: 'regions' as const, label: t('regions.title') }] : []),
          { id: 'updates', label: t('panel.tab.updates') },
        ]}
        value={shown}
        onChange={setTab}
        ariaLabel={t('panel.tabs.aria')}
        class={regionsTab && commentaryOn ? 'panels__feed-tabs panels__feed-tabs--three' : 'panels__feed-tabs'}
      />
      {shown === 'comments' ? (commentary ?? <CommentarySlot />) : shown === 'regions' ? regions : <UpdatesSlot />}
      <InsightsSlot />
    </>
  );
}

/** Seção "Mais sobre a apuração" do `flow` (§4.6.2 linhas 8–10); o AppShell só a monta sem foco. */
export function MorePanels() {
  const route = useRouteState();
  // abaixo do herói e do mapa no celular: monta depois da 1ª pintura (DECISOES §38)
  if (!firstPaintDone.value) return null;
  if (route.office !== 'president')
    return (
      <>
        <CommentarySlot />
        <UpdatesSlot />
        <InsightsSlot />
      </>
    );
  return (
    <>
      <CommentarySlot />
      <RegionsSlot />
      <TrendSlot />
      <UpdatesSlot />
      <InsightsSlot />
    </>
  );
}
